import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FiAlertTriangle, FiInfo, FiRefreshCw, FiSend, FiX, FiXCircle } from 'react-icons/fi';

import { useAuth } from '../../hooks/useAuth';
import { emailService } from '../../services/api';

/**
 * E-mail delivery (v1.42.0) — the answer to "did my e-mails go out?".
 *
 * Every product e-mail is queued in the outbox and handed to the mail server
 * by a worker that stays within the provider's limit (one.com: 25 per 5
 * minutes). This page shows the queue, what went out, what failed and why,
 * and lets the admin retry or cancel a row. Shared with support: they send
 * invitations and login links too, and the API scopes their view to the rows
 * they requested. Bodies are never shown — they can carry login links.
 */

// Mirrors KINDS in backend/services/emailOutbox.js; labels come from i18n.
const KINDS = [
  'invitation',
  'finish_reminder',
  'finish_reminder_copy',
  'daily_missing_hours',
  'daily_approval_reminder',
  'daily_approval_copy',
  'daily_admin_report',
  'weekly_reminder',
  'weekly_admin_report',
  'password_reset',
  'notification',
  'test',
  'other',
];
const STATUSES = ['queued', 'sent', 'failed', 'cancelled'];
const PAGE_SIZE = 25;
// While something is waiting the picture changes every worker tick (1 min)
// or sooner; 10 s keeps the page honest without hammering the API.
const AUTO_REFRESH_MS = 10000;
const SEARCH_DEBOUNCE_MS = 400;
const RUN_RELOAD_DELAY_MS = 2000;
const ERROR_PREVIEW_CHARS = 90;

const Page = styled.div`max-width: 1100px;`;
const Title = styled.h1`font-size: 1.5rem; color: var(--ve-ink-800); margin: 0 0 0.25rem;`;
const Subtitle = styled.p`color: var(--ve-ink-600); margin: 0 0 1.5rem; font-size: 0.9rem;`;
const Card = styled.div`
  background: #fff; border-radius: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 1.5rem; margin-bottom: 1.5rem;
`;
const Tiles = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem;
  @media (max-width: 900px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;
const Tile = styled.div`
  border: 1px solid var(--border-subtle); border-radius: 0.4rem; padding: 0.9rem 1rem; min-width: 0;
`;
const TileLabel = styled.div`font-size: 0.75rem; font-weight: 600; color: var(--ve-ink-600); text-transform: uppercase; letter-spacing: 0.02em;`;
const TileValue = styled.div`
  font-size: 1.6rem; font-weight: 700; margin-top: 0.2rem; line-height: 1.2;
  color: ${({ $tone }) => ($tone === 'warn' ? '#9a7d0a' : $tone === 'bad' ? 'var(--act-danger)' : 'var(--ve-ink-800)')};
`;
const TileMeta = styled.div`font-size: 0.78rem; color: var(--ve-ink-500); margin-top: 0.35rem; line-height: 1.4;`;
const Notice = styled.div`
  display: flex; gap: 0.5rem; align-items: flex-start; font-size: 0.85rem; margin-top: 1rem; padding: 0.6rem 0.8rem; border-radius: 0.4rem;
  color: ${({ $tone }) => ($tone === 'bad' ? 'var(--act-danger)' : $tone === 'warn' ? '#9a7d0a' : 'var(--ve-ink-600)')};
  background: ${({ $tone }) => ($tone === 'bad' ? '#fdedec' : $tone === 'warn' ? '#fef9e7' : 'var(--surface-subtle)')};
  svg { flex-shrink: 0; margin-top: 0.15rem; }
`;
const FilterBar = styled.div`display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center;`;
const Select = styled.select`
  padding: 0.45rem 0.6rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.85rem; background: #fff;
  &:focus { outline: none; border-color: var(--act-primary); }
`;
const Input = styled.input`
  padding: 0.45rem 0.7rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.85rem; min-width: 220px;
  &:focus { outline: none; border-color: var(--act-primary); }
`;
const CheckRow = styled.label`
  display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; color: var(--ve-ink-800); cursor: pointer;
  input { flex-shrink: 0; }
`;
const BatchChip = styled.span`
  display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.78rem; font-weight: 600; padding: 0.25rem 0.6rem; border-radius: 999px;
  background: var(--surface-subtle); color: var(--ve-ink-800); font-family: monospace;
  button { background: none; border: none; cursor: pointer; color: var(--ve-ink-600); display: inline-flex; padding: 0; }
  button:hover { color: var(--act-danger); }
`;
const Spacer = styled.div`flex: 1;`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.5rem 0.9rem; border-radius: 0.25rem; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; border: none; color: #fff; background: var(--act-primary);
  &:hover { filter: brightness(0.94); } &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const GhostBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.35rem; background: none; border: 1px solid var(--border-subtle);
  border-radius: 0.25rem; color: var(--ve-ink-600); font-size: 0.8rem; font-weight: 600; padding: 0.3rem 0.7rem; cursor: pointer;
  &:hover { border-color: var(--act-primary); color: var(--act-primary); } &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const DangerBtn = styled(GhostBtn)`
  &:hover { border-color: var(--act-danger); color: var(--act-danger); }
`;
const Result = styled.div`
  display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; margin-top: 0.75rem;
  color: ${({ $tone }) => ($tone === 'bad' ? 'var(--act-danger)' : $tone === 'warn' ? '#b45309' : '#1e8449')};
`;
const TableWrap = styled.div`
  overflow-x: auto; margin-top: 1rem;
  @media (max-width: 760px) { display: none; }
`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  text-align: left; padding: 0.5rem 0.6rem; font-size: 0.75rem; font-weight: 600; color: var(--ve-ink-600);
  border-bottom: 1px solid var(--border-subtle); white-space: nowrap;
`;
const Td = styled.td`
  padding: 0.55rem 0.6rem; font-size: 0.85rem; color: var(--ve-ink-800); border-bottom: 1px solid var(--surface-subtle);
  vertical-align: top;
`;
const ErrTd = styled(Td)`color: var(--act-danger); font-size: 0.75rem; max-width: 240px; word-break: break-word;`;
const Small = styled.div`font-size: 0.72rem; color: var(--ve-ink-500); margin-top: 0.15rem; word-break: break-all;`;
const CardList = styled.div`
  display: none;
  @media (max-width: 760px) { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 1rem; }
`;
const ItemCard = styled.div`border: 1px solid var(--border-subtle); border-radius: 0.4rem; padding: 0.75rem;`;
const ItemHead = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem;
  font-weight: 600; font-size: 0.9rem; color: var(--ve-ink-800); word-break: break-all;
`;
const ItemMeta = styled.div`color: var(--ve-ink-500); font-size: 0.8rem; margin-top: 0.35rem; word-break: break-all;`;
const ItemError = styled(ItemMeta)`color: var(--act-danger);`;
const ItemActions = styled.div`display: flex; gap: 0.5rem; margin-top: 0.6rem;`;
const Pill = styled.span`
  display: inline-block; font-size: 0.7rem; font-weight: 600; padding: 0.1rem 0.5rem; border-radius: 999px; white-space: nowrap;
  color: ${({ $tone }) => ($tone === 'ok' ? '#1e8449' : $tone === 'warn' ? '#9a7d0a' : $tone === 'bad' ? 'var(--act-danger)' : 'var(--ve-ink-600)')};
  background: ${({ $tone }) => ($tone === 'ok' ? '#eafaf1' : $tone === 'warn' ? '#fef9e7' : $tone === 'bad' ? '#fdedec' : 'var(--surface-subtle)')};
`;
const Pager = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-top: 1rem; font-size: 0.8rem; color: var(--ve-ink-600);
  flex-wrap: wrap;
`;
const Empty = styled.div`padding: 2rem; text-align: center; color: var(--ve-ink-500);`;

const fmtTime = (value) => (value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const fmtDateTime = (value) => (value ? new Date(value).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '');
const STATUS_TONE = { queued: 'warn', sent: 'ok', failed: 'bad', cancelled: 'muted' };

const EmailDelivery = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  // Support only ever sees its own rows (the API enforces it), so the
  // "Only mine" switch is meaningless for them.
  const isAdmin = currentUser?.userType === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = searchParams.get('batchId') || '';

  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [running, setRunning] = useState(false);
  const reloadTimer = useRef(null);

  // Typing in the search box must not fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => () => { if (reloadTimer.current) clearTimeout(reloadTimer.current); }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (status) params.status = status;
      if (kind) params.kind = kind;
      if (batchId) params.batchId = batchId;
      if (onlyMine && isAdmin) params.mine = 'true';
      if (search) params.search = search;
      const [list, sum] = await Promise.all([
        emailService.listOutbox(params),
        emailService.getOutboxSummary(),
      ]);
      setItems((list && list.items) || []);
      setPagination((list && list.pagination) || { total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
      setSummary(sum || null);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message || String(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, status, kind, batchId, onlyMine, isAdmin, search]);

  useEffect(() => { load(); }, [load]);

  const hasQueued = items.some((item) => item.status === 'queued') || Boolean(summary && summary.queued > 0);
  useEffect(() => {
    if (!hasQueued) return undefined;
    const handle = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(handle);
  }, [hasQueued, load]);

  const changeFilter = (setter) => (value) => { setter(value); setPage(1); };
  const clearBatch = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('batchId');
    setSearchParams(next);
    setPage(1);
  };

  const act = async (item, action) => {
    const confirmKey = action === 'retry' ? 'confirmRetry' : 'confirmCancel';
    if (!window.confirm(t(`admin.emailDelivery.${confirmKey}`))) return;
    setBusyId(item.id);
    setNotice(null);
    try {
      if (action === 'retry') await emailService.retryOutbox(item.id);
      else await emailService.cancelOutbox(item.id);
      await load({ silent: true });
    } catch (err) {
      // The login link in that mail has been replaced since: the fix is a new
      // invitation, not a retry, so say that rather than echo the API.
      const text = err && err.code === 'OUTBOX_LINK_ROTATED'
        ? t('admin.emailDelivery.linkRotated')
        : t('admin.emailDelivery.actionError', { message: err.message });
      setNotice({ tone: 'bad', text });
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setNotice(null);
    try {
      const res = await emailService.runOutbox();
      const off = res && res.enabled === false;
      setNotice({ tone: off ? 'warn' : 'ok', text: t(off ? 'admin.emailDelivery.runDisabled' : 'admin.emailDelivery.runScheduled') });
      // The worker runs in the background; give it a moment before reloading.
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(() => load({ silent: true }), RUN_RELOAD_DELAY_MS);
    } catch (err) {
      setNotice({ tone: 'bad', text: t('admin.emailDelivery.actionError', { message: err.message }) });
    } finally {
      setRunning(false);
    }
  };

  const statusLabel = (value) => t(`admin.emailDelivery.status.${value}`, { defaultValue: value });
  const kindLabel = (item) => t(`admin.emailDelivery.kinds.${item.kind}`, { defaultValue: item.kindLabel || item.kind });
  const requestedBy = (item) => (
    item.requestedBy
      ? (item.requestedBy.name || item.requestedBy.email || '—')
      : t('admin.emailDelivery.scheduled')
  );
  // 'attempt 2/6 · next try 09:45' under the pill — the detail that tells an
  // admin whether to wait or to act.
  const statusMeta = (item) => {
    const parts = [];
    if (item.attempts > 0 && item.status !== 'sent') {
      parts.push(t('admin.emailDelivery.attempt', { n: item.attempts, max: item.maxAttempts }));
    }
    if (item.status === 'queued' && item.nextAttemptAt) parts.push(t('admin.emailDelivery.nextTry', { time: fmtTime(item.nextAttemptAt) }));
    if (item.status === 'sent' && item.sentAt) parts.push(t('admin.emailDelivery.sentAt', { time: fmtTime(item.sentAt) }));
    return parts.join(' · ');
  };
  const errorPreview = (item) => {
    if (!item.lastError) return '';
    const text = String(item.lastError);
    return text.length > ERROR_PREVIEW_CHARS ? `${text.slice(0, ERROR_PREVIEW_CHARS)}…` : text;
  };
  const actionsFor = (item) => {
    if (item.status === 'failed' || item.status === 'cancelled') {
      return (
        <GhostBtn type="button" onClick={() => act(item, 'retry')} disabled={busyId === item.id}>
          <FiRefreshCw />
          {t('admin.emailDelivery.actions.retry')}
        </GhostBtn>
      );
    }
    if (item.status === 'queued') {
      return (
        <DangerBtn type="button" onClick={() => act(item, 'cancel')} disabled={busyId === item.id}>
          <FiXCircle />
          {t('admin.emailDelivery.actions.cancel')}
        </DangerBtn>
      );
    }
    return null;
  };

  const rate = summary && summary.rateLimit;
  const windowLine = summary
    ? t(summary.windowResetsAt ? 'admin.emailDelivery.window' : 'admin.emailDelivery.windowOpen', {
      sent: summary.sentInWindow,
      free: summary.capacity,
      time: fmtTime(summary.windowResetsAt),
    })
    : '';

  return (
    <Page>
      <Title>{t('admin.emailDelivery.title')}</Title>
      <Subtitle>{t('admin.emailDelivery.subtitle')}</Subtitle>

      <Card>
        <Tiles>
          <Tile role="group" aria-label={t('admin.emailDelivery.tiles.waiting')}>
            <TileLabel>{t('admin.emailDelivery.tiles.waiting')}</TileLabel>
            <TileValue $tone={summary && summary.queued > 0 ? 'warn' : undefined}>{summary ? summary.queued : '—'}</TileValue>
            {summary && summary.estimatedMinutes > 0 && (
              <TileMeta>{t('admin.emailDelivery.estimate', { minutes: summary.estimatedMinutes })}</TileMeta>
            )}
          </Tile>
          <Tile role="group" aria-label={t('admin.emailDelivery.tiles.sent24h')}>
            <TileLabel>{t('admin.emailDelivery.tiles.sent24h')}</TileLabel>
            <TileValue>{summary ? summary.sent24h : '—'}</TileValue>
          </Tile>
          <Tile role="group" aria-label={t('admin.emailDelivery.tiles.failed7d')}>
            <TileLabel>{t('admin.emailDelivery.tiles.failed7d')}</TileLabel>
            <TileValue $tone={summary && summary.failed7d > 0 ? 'bad' : undefined}>{summary ? summary.failed7d : '—'}</TileValue>
          </Tile>
          <Tile role="group" aria-label={t('admin.emailDelivery.tiles.limit')}>
            <TileLabel>{t('admin.emailDelivery.tiles.limit')}</TileLabel>
            <TileValue>{rate ? rate.description : '—'}</TileValue>
            {summary && <TileMeta>{windowLine}</TileMeta>}
          </Tile>
        </Tiles>

        {summary && summary.cooldownUntil && (
          <Notice $tone="warn">
            <FiAlertTriangle />
            <span>{t('admin.emailDelivery.cooldown', { time: fmtTime(summary.cooldownUntil) })}</span>
          </Notice>
        )}
        {summary && summary.mailConfigured === false && (
          <Notice $tone="bad">
            <FiAlertTriangle />
            <span>{t('admin.emailDelivery.notConfigured')}</span>
          </Notice>
        )}
        {summary && summary.enabled === false && (
          <Notice $tone="muted">
            <FiInfo />
            <span>{t('admin.emailDelivery.workerOff')}</span>
          </Notice>
        )}
      </Card>

      <Card>
        <FilterBar>
          <Select aria-label={t('admin.emailDelivery.filters.status')} value={status} onChange={(e) => changeFilter(setStatus)(e.target.value)}>
            <option value="">{t('admin.emailDelivery.filters.allStatuses')}</option>
            {STATUSES.map((value) => <option key={value} value={value}>{statusLabel(value)}</option>)}
          </Select>
          <Select aria-label={t('admin.emailDelivery.filters.kind')} value={kind} onChange={(e) => changeFilter(setKind)(e.target.value)}>
            <option value="">{t('admin.emailDelivery.filters.allKinds')}</option>
            {KINDS.map((value) => (
              <option key={value} value={value}>{t(`admin.emailDelivery.kinds.${value}`)}</option>
            ))}
          </Select>
          <Input
            type="search"
            aria-label={t('admin.emailDelivery.filters.search')}
            placeholder={t('admin.emailDelivery.filters.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
          />
          {isAdmin && (
            <CheckRow>
              <input type="checkbox" checked={onlyMine} onChange={(e) => changeFilter(setOnlyMine)(e.target.checked)} />
              {t('admin.emailDelivery.filters.onlyMine')}
            </CheckRow>
          )}
          {batchId && (
            <BatchChip>
              {t('admin.emailDelivery.filters.batch', { id: batchId })}
              <button type="button" onClick={clearBatch} title={t('admin.emailDelivery.filters.clearBatch')} aria-label={t('admin.emailDelivery.filters.clearBatch')}>
                <FiX />
              </button>
            </BatchChip>
          )}
          <Spacer />
          <GhostBtn type="button" onClick={() => load()} disabled={loading}>
            <FiRefreshCw />
            {t('admin.emailDelivery.actions.refresh')}
          </GhostBtn>
          <Btn type="button" onClick={runNow} disabled={running || (summary && summary.mailConfigured === false)}>
            <FiSend />
            {t('admin.emailDelivery.actions.sendNow')}
          </Btn>
        </FilterBar>

        {notice && <Result $tone={notice.tone}>{notice.text}</Result>}
        {loadError && <Result $tone="bad">{t('admin.emailDelivery.loadError', { message: loadError })}</Result>}

        {loading && items.length === 0 ? (
          <Empty>{t('admin.emailDelivery.loading')}</Empty>
        ) : items.length === 0 ? (
          <Empty>{t('admin.emailDelivery.empty')}</Empty>
        ) : (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('admin.emailDelivery.columns.created')}</Th>
                    <Th>{t('admin.emailDelivery.columns.kind')}</Th>
                    <Th>{t('admin.emailDelivery.columns.recipient')}</Th>
                    <Th>{t('admin.emailDelivery.columns.subject')}</Th>
                    <Th>{t('admin.emailDelivery.columns.requestedBy')}</Th>
                    <Th>{t('admin.emailDelivery.columns.status')}</Th>
                    <Th>{t('admin.emailDelivery.columns.error')}</Th>
                    <Th>{t('admin.emailDelivery.columns.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <Td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(item.createdAt)}</Td>
                      <Td>{kindLabel(item)}</Td>
                      <Td>
                        {item.recipientName ? <div>{item.recipientName}</div> : null}
                        <Small>{item.to}</Small>
                        {item.cc ? <Small>cc: {item.cc}</Small> : null}
                      </Td>
                      <Td>{item.subject}</Td>
                      <Td>{requestedBy(item)}</Td>
                      <Td>
                        <Pill $tone={STATUS_TONE[item.status] || 'muted'}>{statusLabel(item.status)}</Pill>
                        {statusMeta(item) ? <Small>{statusMeta(item)}</Small> : null}
                      </Td>
                      <ErrTd title={item.lastError || undefined}>{errorPreview(item)}</ErrTd>
                      <Td>{actionsFor(item)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            <CardList>
              {items.map((item) => (
                <ItemCard key={item.id}>
                  <ItemHead>
                    <span>{item.recipientName || item.to}</span>
                    <Pill $tone={STATUS_TONE[item.status] || 'muted'}>{statusLabel(item.status)}</Pill>
                  </ItemHead>
                  <ItemMeta>{item.to}</ItemMeta>
                  <ItemMeta>{kindLabel(item)} · {fmtDateTime(item.createdAt)}</ItemMeta>
                  <ItemMeta>{item.subject}</ItemMeta>
                  <ItemMeta>{t('admin.emailDelivery.columns.requestedBy')}: {requestedBy(item)}</ItemMeta>
                  {statusMeta(item) ? <ItemMeta>{statusMeta(item)}</ItemMeta> : null}
                  {item.lastError ? <ItemError title={item.lastError}>{errorPreview(item)}</ItemError> : null}
                  {actionsFor(item) ? <ItemActions>{actionsFor(item)}</ItemActions> : null}
                </ItemCard>
              ))}
            </CardList>

            <Pager>
              <span>
                {t('admin.emailDelivery.pageInfo', { page: pagination.page, pages: pagination.totalPages || 1, total: pagination.total })}
                {hasQueued ? ` · ${t('admin.emailDelivery.autoRefresh')}` : ''}
              </span>
              <span style={{ display: 'flex', gap: '0.5rem' }}>
                <GhostBtn type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1 || loading}>
                  {t('common.previous')}
                </GhostBtn>
                <GhostBtn type="button" onClick={() => setPage((p) => p + 1)} disabled={pagination.page >= (pagination.totalPages || 1) || loading}>
                  {t('common.next')}
                </GhostBtn>
              </span>
            </Pager>
          </>
        )}
      </Card>
    </Page>
  );
};

export default EmailDelivery;
